"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { Icon } from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

/**
 * Scroll surface for step content. When the content overflows, a bottom fade
 * plus a clickable "Scroll for more" pill make the hidden content obvious;
 * both disappear once the user reaches the end.
 *
 * Uses the app `ScrollArea` (overlay scrollbar) rather than `overflow-y-auto`
 * so a native scrollbar can't eat into the right gutter — left and right
 * content padding stay equal.
 */
export function StepScrollArea({
  children,
  contentClassName,
}: {
  children: React.ReactNode;
  contentClassName?: string;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const update = () => {
      setCanScrollMore(
        viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 12,
      );
    };
    update();
    viewport.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    // Content height changes too (e.g. the CSV columns card appearing).
    if (viewport.firstElementChild)
      observer.observe(viewport.firstElementChild);
    return () => {
      viewport.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  const scrollMore = useCallback(() => {
    const viewport = viewportRef.current;
    viewport?.scrollBy({
      top: viewport.clientHeight * 0.8,
      behavior: "smooth",
    });
  }, []);

  return (
    // Flex-sized down to the viewport: the mobile drawer is only max-height
    // capped, so a percentage height here would resolve to auto and overflow.
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ScrollArea
        className="flex min-h-0 flex-1 flex-col"
        viewportClassName="min-h-0 flex-1"
        viewportRef={viewportRef}
        constrainContentWidth
      >
        <div className={cn("flex flex-col", contentClassName)}>{children}</div>
      </ScrollArea>
      <AnimatePresence initial={false}>
        {canScrollMore && (
          <motion.div
            key="scroll-more"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center"
          >
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-surface-elevated to-transparent" />
            <button
              type="button"
              onClick={scrollMore}
              className={cn(
                "pointer-events-auto relative mb-1 flex cursor-pointer items-center gap-1 rounded-full",
                "border border-border bg-surface-elevated px-2.5 py-1 text-[11px] font-medium text-muted-foreground",
                "shadow-[0_1px_2px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.10)]",
                "transition-[color,scale] hover:text-foreground active:scale-[0.96]",
                // Extend the hit area without growing the pill visually.
                "before:absolute before:-inset-2 before:content-['']",
              )}
            >
              Scroll for more
              <Icon name="IconArrowDown" size={12} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
